import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getAudioVersions, selectAudioVersion, AudioVersion } from '../../services/workflowAPI';
import { logger } from '../../utils/logger';

interface AudioVersionSelectorProps {
  conversationId: string;
  onVersionSelected?: (version: AudioVersion) => void;
  className?: string;
}

export const AudioVersionSelector: React.FC<AudioVersionSelectorProps> = ({
  conversationId,
  onVersionSelected,
  className = '',
}) => {
  const [versions, setVersions] = useState<AudioVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVersions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getAudioVersions(conversationId);
      if (result.success) {
        setVersions(result.versions);
        setSelectedVersionId(result.selectedVersionId);
      } else {
        setError('Failed to load audio versions');
      }
    } catch (err) {
      logger.error('Error loading audio versions:', err);
      setError('Error loading audio versions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, [conversationId]);

  const handleSelectVersion = async (versionId: string) => {
    setIsSelecting(true);
    setError(null);
    try {
      const result = await selectAudioVersion(conversationId, versionId);
      if (result.success && result.selectedVersion) {
        setSelectedVersionId(versionId);
        onVersionSelected?.(result.selectedVersion);
        logger.info('Audio version selected successfully:', versionId);
      } else {
        setError(result.error || 'Failed to select audio version');
      }
    } catch (err) {
      logger.error('Error selecting audio version:', err);
      setError('Error selecting audio version');
    } finally {
      setIsSelecting(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className={`p-4 text-center ${className}`}>
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Loading audio versions...
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return null; // Don't show anything if there are no versions
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Audio Versions ({versions.length})
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={loadVersions}
          className="h-7 px-2 text-xs"
          disabled={isLoading}
        >
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </Button>
      </div>

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {versions.map((version) => {
          const isSelected = version.version_id === selectedVersionId;

          return (
            <div
              key={version.version_id}
              className={`
                relative rounded-lg border p-3 transition-all duration-200
                ${isSelected
                  ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30 shadow-sm'
                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-600'
                }
              `}
            >
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <Badge variant="default" className="text-xs bg-emerald-500 text-white">
                    Selected
                  </Badge>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-start justify-between pr-16">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-slate-900 dark:text-slate-100">
                        {version.voice_name}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {version.language_code}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      {formatDuration(version.duration)} • {formatDate(version.created_at)}
                    </div>
                  </div>
                </div>

                {version.instructions && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 italic line-clamp-2">
                    "{version.instructions}"
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <audio
                    controls
                    preload="metadata"
                    className="flex-1 h-8"
                    style={{ maxHeight: '32px' }}
                  >
                    <source src={version.url} type="audio/mpeg" />
                  </audio>

                  {!isSelected && (
                    <Button
                      size="sm"
                      onClick={() => handleSelectVersion(version.version_id)}
                      disabled={isSelecting}
                      className="h-8 px-3 bg-primary hover:bg-primary/90 text-xs"
                    >
                      {isSelecting ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
